import React, { useState, useEffect}  from 'react';
import { DataContext } from '../App';
import { Line } from "react-chartjs-2";
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

ChartJS.register(annotationPlugin);

const {getPadding, average, min, max} = require('../utils.js');
const {lerpColor, linSpace, clamp} = require('../colorutils.js');

// @TO_DO: this needs to be tidied up parameters properly passed?
function getBlobColor(value) {

    const upperThresh = 12.0
    const lowerThresh = 5
    const chartMax = 100
    const chartMin = -100
    const colors = {
        red: '#a10303',
        yellow: '#f5d742',
        amber: '#FF9800',
        green: '#34d3a1',
        blue: '#345ed3',
        black: '#000000'
    };

    if (value > chartMax) {
        return colors.red
    }
    else if (value > upperThresh && value <= chartMax) {
        value = (value - upperThresh) / (chartMax - upperThresh)
        return lerpColor(colors.amber, colors.red, value)
    }
    else if ((value >= lowerThresh) && value <= upperThresh) {
        return colors.yellow
    }
    else if (value < lowerThresh && value >= chartMin) {
        value = (lowerThresh - value) / (lowerThresh - chartMin)
        return lerpColor(colors.green, colors.blue, value)
    }
    else if (value < chartMin) {
        return colors.blue
    }
    else {
        return colors.black
    }
}


function Forecast(){

    const data = React.useContext(DataContext);
    /**
        definitions and constants
    **/
    const today = new Date(data.prediction_date)
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    
    /** 
        Stuff that comes from the backend and it's derivatives
        No AJAX calls in serverless, how to hide the data
    **/
    var average_fruitfly_per_trap_next_5_days = data.predicted_daily_fruit_fly_pressure
    var average_fruitfly_per_trap_last_14_days = data.historical_daily_fruit_fly_pressure

    var nHist = average_fruitfly_per_trap_last_14_days.length
    var nForecast = average_fruitfly_per_trap_next_5_days.length

    var forecastMax = max(average_fruitfly_per_trap_last_14_days.concat(average_fruitfly_per_trap_next_5_days));
    var forecastMin = min(average_fruitfly_per_trap_last_14_days.concat(average_fruitfly_per_trap_next_5_days));
    var last_14_day_average = average(average_fruitfly_per_trap_last_14_days);

    // Get the x-labels for the forecasted part of the plot
    var idx = today.getDay()
    var forecast_labels = ["Today", days[idx + 1], days[idx + 2], days[idx + 3], days[idx + 4]];
    // Get the labels for the historical part of the plot
    var history_labels = []
    var history_start = new Date(today.setUTCDate(today.getUTCDate() - (nHist + 1)));
    for (let i = 0; i < nHist; i++) {
        var item = new Date(history_start.setUTCDate(history_start.getUTCDate() + 1));
        history_labels.push(item)
    }
    history_labels = history_labels.map(t => t.getDate().toString() + " " + months[t.getMonth()])
    
    // We display forecast and history points as seperate datasets
    // So we need to pad them with NaNs for chart.js todo it
    var joinidx = nHist // index where the plots 'join' - length of whatever history we send from backend - currntly 14
    var forecast_data = getPadding(nHist, NaN).concat(average_fruitfly_per_trap_next_5_days); // pad history dataset
    var history_data = average_fruitfly_per_trap_last_14_days.concat(getPadding(nForecast, NaN)); // pad forecast dataset
    history_data[joinidx] = average_fruitfly_per_trap_next_5_days[0] // this is joint value for "today" - not needed if graph has no joining lines
    var labels = history_labels.concat(forecast_labels); // join the labels

    // colors for the forecasted/historical values plots
    var history_colors = history_data.slice(0, joinidx + 1);
    history_colors = history_colors.map(x => 100.0 * ((x - last_14_day_average) / last_14_day_average));
    history_colors = history_colors.map(x => getBlobColor(x));
    history_colors = history_colors.concat(getPadding(nForecast, ""))

    var forecast_colors = average_fruitfly_per_trap_next_5_days;
    forecast_colors = forecast_colors.map(x => 100.0 * ((x - last_14_day_average) / last_14_day_average));
    forecast_colors = forecast_colors.map(x => getBlobColor(x));
    forecast_colors = getPadding(nHist, "").concat(forecast_colors)

    // variable font size depending on the screen size
    const [windowWidth, setWindowWidth] = useState(0);
    const updateDimensions = () => {
        const windowWidth = window.innerWidth
        setWindowWidth(windowWidth)
    }
    useEffect(() => { 
        updateDimensions();
        window.addEventListener("resize", updateDimensions);
        return () => window.removeEventListener("resize",updateDimensions);
    }, []);

    const fs = windowWidth > 1600 ? 12 : 8;
    const point_radius = windowWidth > 1600 ? 8 : 4;

    const chartdata = {
        labels: labels,
        datasets: [{
            data: history_data,
            cubicInterpolationMode: 'monotone',
            borderWidth: 1,
            tension: 0.4,
            borderColor: 'rgb(50, 50, 50, 0.5)',
            backgroundColor: history_colors,
            pointStyle: 'rectRounded',
            pointRadius: point_radius,
            pointHoverRadius: point_radius + 1,
        }, {
            data: forecast_data,
            borderDash: [6, 6],
            borderWidth: 1,
            cubicInterpolationMode: 'monotone',
            tension: 0.4,
            borderColor: 'rgb(50, 50, 50, 0.7)',
            backgroundColor: forecast_colors,
            pointStyle: 'rectRounded',
            pointRadius: point_radius,
            pointHoverRadius: point_radius + 1
        }]
    };
    
    const options = {
        scales: {
            y: {
                beginAtZero: false,
                max: forecastMax + 1.5,
                min: forecastMin - 0.05,
                grid: {
                    display: false,
                    drawTicks: true
                },
                ticks: {
                    display: false,
                },
                display: true,
                offset: true
            },
            x: {
                grid: {
                    display: false,
                    drawTicks: false
                },
                ticks: {
                    display: true,
                    maxRotation: 45,
                    minRotation: 45
                }
            }
        },
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: false
            },
            annotation: {
                annotations: {
                    annotation1: {
                        type: 'line',
                        borderColor: 'black',
                        drawTime: 'beforeDatasetsDraw',
                        borderWidth: 1,
                        borderDash: [6, 3],
                        //display: (ctx) => ctx.chart.isDatasetVisible(1),
                        label: {
                            enabled: false,
                            content: 'Now',
                            position: {
                                x: 'center',
                                y: 'center'
                            }
                        },
                        scaleID: 'x',
                        value: forecast_labels[0]
                    },
                    annotation2: {
                        type: 'line',
                        borderColor: 'black',
                        drawTime: 'beforeDatasetsDraw',
                        borderWidth: 1.5,
                        // display: (ctx) => ctx.chart.isDatasetVisible(1),
                        label: {
                            enabled: true,
                            drawTime: 'afterDatasetsDraw',
                            content: 'Current level: ' + last_14_day_average.toFixed( 2),
                            position: 'start',
                            rotation: 0,
                            yAdjust: 18,
                            font: {
                                size: fs
                            },
                        },
                        scaleID: 'y',
                        value: last_14_day_average
                    },
                    annotation3: {
                        type: 'box',
                        backgroundColor: 'rgba(255, 245, 157, 0.1)',
                        borderWidth: 0,
                        drawTime: 'beforeDatasetsDraw',
                        xMax: 14.0,
                        xMin: 0.0,
                        label: {
                            drawTime: 'afterDraw',
                            enabled: true,
                            content: ['Last', '14 days'],
                            position: {
                                x: 'start',
                                y: 'start'
                            },
                            font: {
                                size: fs
                            },
                        }
                    },
                    annotation4: {
                        type: 'box',
                        backgroundColor: 'rgba(255, 157, 157, 0.1)',
                        borderWidth: 0,
                        drawTime: 'beforeDatasetsDraw',
                        xMax: 14.0,
                        xMin: 18.0,
                        label: {
                            drawTime: 'afterDraw',
                            enabled: true,
                            content: ['Next','5 days'],
                            position: {
                                x: 'start',
                                y: 'start'
                            },
                            font: {
                                size: fs
                            },
                        }
                    }
                },
            }
        }
    }

    return(
        <div className="card-body">
            <div style={{height:"180px"}}>
                <Line
                    data={chartdata}
                    options={options}
                />
            </div>
        </div>

    );

}

export default Forecast;